import { z } from '../zod.js';
import {
  CursorPaginationValidationBaseSchema,
  SellerAPICursorPaginationValidationSchema,
  withCursorPaginationValidation,
} from '../shared/cursorPagination';
import { ObjectIdStringValidationSchema } from '../shared/ObjectId';
import {
  LivemodeFilterValidationSchema,
  LivemodeQueryParamValidationSchema,
} from '../shared/livemode';
import { INVOICE_STATUS, InvoiceStatusValidationEnum } from './enum';

// Cap free-text filter inputs so a long string can't blow up regex compilation
// or the size of the `$or` payload sent to mongo.
const FILTER_TEXT_MAX = 200;

const filterText = (max: number) => z.string().trim().min(1).max(max);

const invoiceListFilterFields = {
  search: filterText(FILTER_TEXT_MAX).optional().meta({
    description:
      'Case-insensitive search matched against `customerEmail`, `customerName`, `productTitle` and `invoiceNumber`. The query is routed based on input shape: email-like inputs (`jane@example.com`) match `customerEmail` only; identifier-like inputs containing a digit/underscore/hyphen (`2026-000010`) match `invoiceNumber`; multi-word inputs match `customerName` and `productTitle`; any other single bare word falls back to a broad case-insensitive substring match across `customerName`, `customerEmail` and `productTitle`. Best default when the user gives an ambiguous query. A customer may have multiple invoice records — always review all matches, not just the first.',
  }),
  status: InvoiceStatusValidationEnum.optional().meta({
    description: `Filter by invoice status. Only one status per call. Valid values: ${INVOICE_STATUS.join(', ')}. Use "paid" for completed invoices, "open" for outstanding, "refunded" or "partially_refunded" for refund history, "void" for cancelled. Filter by status when counting a category — an unfiltered total includes drafts and voids.`,
  }),
  customerId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return all invoices for a specific customer. Useful when you already have the customer record (e.g. from get_customer or list_customers).',
  }),
  chargeId: ObjectIdStringValidationSchema.optional().meta({
    description:
      'Return invoices for a specific payment (Charge). Use when a customer asks for a copy of their invoice and you already have the originating payment ID. Most charges have one invoice.',
  }),
  poNumber: filterText(FILTER_TEXT_MAX).optional().meta({
    description: 'Exact match on the invoice PO number. Use for "find the invoice for PO-5678".',
  }),
  createdAfter: z.iso.datetime().optional().meta({
    description:
      'Inclusive lower bound on invoice createdAt. ISO-8601 UTC datetime with the `Z` suffix (e.g. `2026-01-01T00:00:00Z`). Never use a local timezone offset — invoices are stored and compared in UTC.',
  }),
  createdBefore: z.iso.datetime().optional().meta({
    description:
      'Inclusive upper bound on invoice createdAt. ISO-8601 UTC datetime with the `Z` suffix (e.g. `2026-12-31T23:59:59Z`). Never use a local timezone offset — invoices are stored and compared in UTC.',
  }),
} as const;

const dateRangeRefine = (data: { createdAfter?: string; createdBefore?: string }) =>
  !(
    data.createdAfter &&
    data.createdBefore &&
    Date.parse(data.createdAfter) > Date.parse(data.createdBefore)
  );

const dateRangeRefineMessage = {
  message: 'createdAfter must be earlier than or equal to createdBefore',
  path: ['createdBefore'] as string[],
};

// Two list-schema variants, one filter set:
// - `InvoiceListQueryValidationSchema` uses `CursorPaginationValidationBaseSchema`
//   — native zod types (`limit: number`). Consumed by the MCP tool, which
//   receives JSON inputs already typed.
// - `InvoiceListSellerAPIQueryValidationSchema` uses
//   `SellerAPICursorPaginationValidationSchema` — string→number coercion on
//   `limit` because URL query params arrive as strings. Consumed by the
//   Koa controller.
// Filter fields (`invoiceListFilterFields`) and the date-range refine are
// shared between both variants. `livemode` differs the same way as `limit`: a
// boolean on the MCP variant, the `'true'`/`'false'` query string on the seller API.
export const InvoiceListQueryValidationSchema = withCursorPaginationValidation(
  CursorPaginationValidationBaseSchema.extend({
    ...invoiceListFilterFields,
    livemode: LivemodeFilterValidationSchema,
  }).refine(dateRangeRefine, dateRangeRefineMessage),
);

export const InvoiceListSellerAPIQueryValidationSchema = withCursorPaginationValidation(
  SellerAPICursorPaginationValidationSchema.extend({
    ...invoiceListFilterFields,
    livemode: LivemodeQueryParamValidationSchema,
  }).refine(dateRangeRefine, dateRangeRefineMessage),
);
